<template>
    <div class="tab-pane active">
        <div v-if="notCheckedInReport.length > 0" class="table-wrapper no-print">
            <div v-show="searchKeyword || filteredAAreasList.length > 0" class="time-window-label">
                {{ $t('checkIn.notCheckedInTab.timeWindowLabel', { beginTime, endTime }) }}
            </div>
            <table
                v-for="(aArea, index) in filteredAAreasList"
                v-show="!searchKeyword || filteredAAreasList.length > 0"
                :key="index"
                class="common-table not-check-in"
            >
                <thead>
                    <tr class="tbl-header">
                        <th class="not-checked">{{ aArea.name }}</th>
                    </tr>
                </thead>
                <tbody v-if="aArea.locations.length > 0">
                    <tr
                        v-for="(row, index2) in aArea.locations"
                        v-show="deepSearchKeyword(row, aArea)"
                        :key="index2"
                    >
                        <td>{{ getLocationAndDeviceName(row) }}</td>
                    </tr>
                </tbody>
                <tbody v-else class="no-reports">
                    <tr>
                        <td>{{ $t('notCheckIn.notCheckedInFull.NoReports') }}</td>
                    </tr>
                </tbody>
            </table>
            <h3 v-if="searchKeyword && filteredAAreasList.length === 0">
                {{ $t('common.searchNoResult', { val: searchKeyword }) }}
            </h3>
        </div>
        <div v-else class="data-empty no-print">
            <h3
                v-if="dataLoaded"
                v-text="
                    beginTime !== '--:--'
                        ? $t('checkIn.notCheckedInTab.noResidents', { beginTime, endTime })
                        : $t('checkIn.notCheckedInTab.noDataLoaded')
                "
            ></h3>
        </div>
        <div class="print-only">
            <h4 v-if="searchKeyword && filteredAAreasList.length > 0">
                {{ $t('print.search', { val: searchKeyword }) }}
            </h4>
            <p v-text="$t('checkIn.notCheckedInTab.timeWindowLabel', { beginTime, endTime })"></p>
            <template v-if="notCheckedInReport.length > 0">
                <table
                    v-for="(aArea, index) in printedAAreasList"
                    :key="index"
                    class="table print-notcheckedin"
                >
                    <thead>
                        <tr class="tbl-header">
                            <th>{{ aArea.name }}</th>
                        </tr>
                    </thead>
                    <tbody v-if="aArea.locations.length > 0">
                        <tr
                            v-for="(row, index2) in aArea.locations"
                            v-show="
                                deepSearchKeyword(row, aArea) ||
                                    (searchKeyword && filteredAAreasList.length === 0)
                            "
                            :key="index2"
                        >
                            <td>{{ getLocationAndDeviceName(row) }}</td>
                        </tr>
                    </tbody>
                    <tbody v-else class="no-reports">
                        <tr>
                            <td>{{ $t('notCheckIn.notCheckedInFull.NoReports') }}</td>
                        </tr>
                    </tbody>
                </table>
            </template>
            <div v-else class="data-empty print-only">
                <h3
                    v-text="
                        beginTime !== '--:--'
                            ? $t('checkIn.notCheckedInTab.noResidents', { beginTime, endTime })
                            : $t('checkIn.notCheckedInTab.noDataLoaded')
                    "
                ></h3>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import mappingState from '../common/mappingState';
import {
    resourceNotCheckedInFull,
    resourceCheckIn,
    resourceNotCheckInConfiguration,
} from '../../api/resources';
import { getCancelToken } from '../../api';
import { sortLocations, ifIsNotCanceled, formatTime } from '../common/helpers';

export default {
    mixins: [mappingState],

    data() {
        return {
            notCheckedInReport: [],
            dataLoaded: false,
            cancelToken: getCancelToken(),
            beginTime: '--:--',
            endTime: '--:--',
        };
    },

    computed: {
        filteredAAreasList() {
            let keyword = this.searchKeyword;
            return keyword
                ? this.notCheckedInReport.filter(
                      item =>
                          item.name.toLowerCase().includes(keyword) ||
                          item.locations.some(
                              location =>
                                  location.name.toLowerCase().includes(keyword) ||
                                  location.devices.some(device => device.toLowerCase().includes(keyword))
                          )
                  )
                : this.notCheckedInReport;
        },

        printedAAreasList() {
            return sortLocations(
                this.filteredAAreasList.length > 0 ? this.filteredAAreasList : this.notCheckedInReport
            );
        },
    },

    mounted() {
        this.showLoading(true);
        this.loadCheckIn()
            .then(() =>
                this.loadNotCheckInConfiguration().then(response => this.loadNotCheckedIn(response.data))
            )
            .catch(ifIsNotCanceled());
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        /**
         * Checks if location matches to current search keyword
         * @param {object} name Object key
         * @returns {Boolean}
         */
        matchesKeyword(name) {
            return !!this.searchKeyword && name.toLowerCase().includes(this.searchKeyword);
        },

        /**
         * Checks if location or device matches to current search keyword
         * @param {object} row Location object
         * @param {object} aArea Assignment area object
         * @returns {Boolean}
         */
        deepSearchKeyword(row, aArea) {
            return (
                !this.searchKeyword ||
                this.matchesKeyword(row.name) ||
                this.matchesKeyword(aArea.name) ||
                row.devices.some(device => this.matchesKeyword(device))
            );
        },

        /**
         * Returns location and device name
         * @param {object} row Location object
         * @returns {String}
         */
        getLocationAndDeviceName(row) {
            return row.devices.length > 0 ? row.name.concat(' (' + row.devices.join(', ') + ')') : row.name;
        },

        /**
         * Load begin & end times for not checked in tab
         */
        loadCheckIn() {
            return resourceCheckIn({ cancelToken: this.cancelToken.token })
                .then(response => response.data)
                .then(data => {
                    this.beginTime = formatTime(data.begin_time.slice(0, 5));
                    this.endTime = formatTime(data.end_time.slice(0, 5));
                });
        },

        /**
         * Load configuration for not checked in tab
         */
        loadNotCheckInConfiguration() {
            return resourceNotCheckInConfiguration({ cancelToken: this.cancelToken.token });
        },

        /**
         * Load information for not checked in tab
         */
        loadNotCheckedIn(data) {
            return resourceNotCheckedInFull({
                data: { areas: data.assignment_areas },
                cancelToken: this.cancelToken.token,
            }).then(response => {
                this.notCheckedInReport = response.data ? this.sortCheckedInReport(response.data) : [];
                this.dataLoaded = true;
                this.showLoading(false);
                this.setSearchEnabled(this.notCheckedInReport.length > 0);
            });
        },
        sortCheckedInReport(data) {
            return data.map(aa => {
                aa.locations = sortLocations(aa.locations);
                return aa;
            });
        },
    },
};
</script>
